import { supabase } from './client';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  subscription_tier: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Campaign {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'archived';
  goal_type?: string;
  target_representatives: number;
  geography_config: Record<string, unknown>;
  start_date?: string;
  deadline?: string;
  survey_template_id?: string;
  access_mode: 'authenticated' | 'pin' | 'anonymous';
  require_gps: boolean;
  rate_limit_mode: 'per_place' | 'per_campaign' | 'per_device';
  max_responses_per_device?: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SurveyTemplate {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  schema: Record<string, unknown>;
  ui_schema: Record<string, unknown>;
  settings: Record<string, unknown>;
  version: number;
  is_archived: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface TargetLocation {
  id: string;
  campaign_id: string;
  name: string;
  address?: string;
  city?: string;
  region?: string;
  country?: string;
  lat?: number;
  lng?: number;
  type?: string;
  custom_fields: Record<string, unknown>;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped';
  assigned_surveyor_id?: string;
  _deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface SurveyResponse {
  id: string;
  campaign_id: string;
  surveyor_id?: string;
  target_location_id?: string;
  survey_data: Record<string, unknown>;
  status: 'pending' | 'approved' | 'rejected';
  review_notes?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  response_type: 'assigned' | 'pin' | 'anonymous';
  device_fingerprint?: string;
  gps_location?: { lat: number; lng: number };
  gps_verified: boolean;
  started_at?: string;
  submitted_at?: string;
  device_info: Record<string, unknown>;
  _deleted: boolean;
  created_at: string;
  updated_at: string;
}

export const api = {
  async getCampaigns(organizationId: string) {
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as Campaign[];
  },

  async getCampaign(id: string) {
    const { data, error } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as Campaign;
  },

  async createCampaign(campaign: Partial<Campaign>) {
    const { data, error } = await supabase
      .from('campaigns')
      .insert(campaign)
      .select()
      .single();
    
    if (error) throw error;
    return data as Campaign;
  },

  async updateCampaign(id: string, updates: Partial<Campaign>) {
    const { data, error } = await supabase
      .from('campaigns')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data as Campaign;
  },

  async deleteCampaign(id: string) {
    const { error } = await supabase
      .from('campaigns')
      .update({ status: 'archived' })
      .eq('id', id);
    
    if (error) throw error;
  },

  async getSurveyTemplates(organizationId: string) {
    const { data, error } = await supabase
      .from('survey_templates')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('is_archived', false)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as SurveyTemplate[];
  },

  async createSurveyTemplate(template: Partial<SurveyTemplate>) {
    const { data, error } = await supabase
      .from('survey_templates')
      .insert(template)
      .select()
      .single();
    
    if (error) throw error;
    return data as SurveyTemplate;
  },

  async updateSurveyTemplate(id: string, updates: Partial<SurveyTemplate>) {
    const { data, error } = await supabase
      .from('survey_templates')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data as SurveyTemplate;
  },

  async getTargetLocations(campaignId: string) {
    const { data, error } = await supabase
      .from('target_locations')
      .select('*')
      .eq('campaign_id', campaignId)
      .eq('_deleted', false)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data as TargetLocation[];
  },

  async createTargetLocation(location: Partial<TargetLocation>) {
    const { data, error } = await supabase
      .from('target_locations')
      .insert(location)
      .select()
      .single();
    
    if (error) throw error;
    return data as TargetLocation;
  },

  async getCampaignResponses(campaignId: string) {
    const { data, error } = await supabase
      .from('survey_responses')
      .select(`
        *,
        surveyor:users!surveyor_id(full_name, email),
        target_location:target_locations!target_location_id(name, address)
      `)
      .eq('campaign_id', campaignId)
      .eq('_deleted', false)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async updateResponseStatus(
    responseId: string,
    status: 'approved' | 'rejected',
    reviewNotes?: string
  ) {
    const { data, error } = await supabase
      .from('survey_responses')
      .update({
        status,
        review_notes: reviewNotes,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', responseId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async subscribeToResponses(campaignId: string, callback: (payload: unknown) => void) {
    return supabase
      .channel(`responses:${campaignId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'survey_responses',
          filter: `campaign_id=eq.${campaignId}`,
        },
        callback
      )
      .subscribe();
  },

  async subscribeToLocations(campaignId: string, callback: (payload: unknown) => void) {
    return supabase
      .channel(`locations:${campaignId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'target_locations',
          filter: `campaign_id=eq.${campaignId}`,
        },
        callback
      )
      .subscribe();
  },
};
